import React, { useState } from 'react';
import { DollarSignIcon, ClockIcon, CheckCircleIcon, XCircleIcon, FileTextIcon, DownloadIcon, TruckIcon, AlertTriangleIcon, BarChart4Icon, CalendarIcon } from 'lucide-react';
import { TabNavigation } from '../../dashboard/TabNavigation';
import { PendingRequests } from './PendingRequests';
import { ApprovedRequests } from './ApprovedRequests';
import { ACHGenerator } from './ACHGenerator';
import { RequestDetails } from './RequestDetails';
export const FactoringDashboard = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [showRequestDetails, setShowRequestDetails] = useState(false);
  // Sample data for the dashboard
  const stats = {
    pendingRequests: 12,
    approvedToday: 8,
    deniedToday: 2,
    totalPending: 28000.75,
    totalApprovedToday: 18500.25,
    totalDeniedToday: 4500.5,
    achFilesGenerated: 3,
    totalTruckingCompanies: 15,
    activeCompanies: 12
  };
  // Sample pending requests data
  const pendingRequests = [{
    id: 'req-001',
    truckingCompany: 'Mountain Valley Trucking',
    jobNumber: 'JOB-2024-001',
    jobName: 'Downtown Commercial Complex Phase 1',
    completionDate: '2024-01-15',
    requestDate: '2024-01-16',
    amount: 2450.75,
    invoiceNumber: 'INV-MV-12345',
    status: 'pending',
    priority: 'high',
    contractor: 'ABC Construction',
    contractorRating: 'A',
    completedLoads: 12,
    totalHours: 9.5,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_12345.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_12345.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_12345.pdf'
    }],
    notes: 'Regular client with excellent payment history.',
    paymentTerms: 'Net 30',
    factorRate: 2.5
  }, {
    id: 'req-002',
    truckingCompany: 'Wasatch Hauling',
    jobNumber: 'JOB-2024-003',
    jobName: 'Residential Development Phase 2',
    completionDate: '2024-01-15',
    requestDate: '2024-01-16',
    amount: 1875.5,
    invoiceNumber: 'INV-WH-54321',
    status: 'pending',
    priority: 'medium',
    contractor: 'Mountain Development LLC',
    contractorRating: 'B',
    completedLoads: 8,
    totalHours: 7.25,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_54321.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_54321.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_54321.pdf'
    }],
    notes: 'New client, verify load tickets carefully.',
    paymentTerms: 'Net 45',
    factorRate: 3.0
  }, {
    id: 'req-003',
    truckingCompany: 'SLC Trucking Services',
    jobNumber: 'JOB-2024-005',
    jobName: 'Highway 89 Expansion Project',
    completionDate: '2024-01-15',
    requestDate: '2024-01-16',
    amount: 3200.0,
    invoiceNumber: 'INV-SLC-67890',
    status: 'pending',
    priority: 'high',
    contractor: 'State DOT',
    contractorRating: 'A+',
    completedLoads: 14,
    totalHours: 10.5,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_67890.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_67890.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_67890.pdf'
    }],
    notes: 'Government project, secure payment but longer processing time.',
    paymentTerms: 'Net 60',
    factorRate: 3.5
  }, {
    id: 'req-004',
    truckingCompany: 'Summit Transport',
    jobNumber: 'JOB-2024-007',
    jobName: 'Commercial Plaza Foundation',
    completionDate: '2024-01-14',
    requestDate: '2024-01-15',
    amount: 1950.25,
    invoiceNumber: 'INV-ST-24680',
    status: 'pending',
    priority: 'low',
    contractor: 'Premier Builders Inc',
    contractorRating: 'B+',
    completedLoads: 10,
    totalHours: 8.0,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_24680.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_24680.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_24680.pdf'
    }],
    notes: 'Consistent client with good payment record.',
    paymentTerms: 'Net 30',
    factorRate: 2.75
  }, {
    id: 'req-005',
    truckingCompany: 'Alpine Delivery',
    jobNumber: 'JOB-2024-009',
    jobName: 'School Expansion Project',
    completionDate: '2024-01-14',
    requestDate: '2024-01-15',
    amount: 2100.0,
    invoiceNumber: 'INV-AD-13579',
    status: 'pending',
    priority: 'medium',
    contractor: 'Education Development Corp',
    contractorRating: 'A-',
    completedLoads: 9,
    totalHours: 8.25,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_13579.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_13579.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_13579.pdf'
    }],
    notes: 'Education project, reliable payment but requires complete documentation.',
    paymentTerms: 'Net 45',
    factorRate: 3.0
  }];
  // Sample approved requests data
  const approvedRequests = [{
    id: 'req-006',
    truckingCompany: 'Mountain Valley Trucking',
    jobNumber: 'JOB-2023-095',
    jobName: 'Downtown Water Main Replacement',
    completionDate: '2024-01-13',
    requestDate: '2024-01-14',
    approvalDate: '2024-01-15',
    amount: 2850.0,
    invoiceNumber: 'INV-MV-11111',
    status: 'approved',
    priority: 'high',
    contractor: 'City Public Works',
    contractorRating: 'A',
    completedLoads: 7,
    totalHours: 7.5,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_11111.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_11111.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_11111.pdf'
    }],
    notes: 'Approved and processed for payment.',
    paymentTerms: 'Net 60',
    factorRate: 3.25,
    achFileName: 'ACH_20240115_MV_11111.ach',
    achGenerationDate: '2024-01-15',
    expectedPaymentDate: '2024-01-16'
  }, {
    id: 'req-007',
    truckingCompany: 'Wasatch Hauling',
    jobNumber: 'JOB-2023-087',
    jobName: 'Main Street Repaving Project',
    completionDate: '2024-01-13',
    requestDate: '2024-01-14',
    approvalDate: '2024-01-15',
    amount: 2200.5,
    invoiceNumber: 'INV-WH-22222',
    status: 'approved',
    priority: 'medium',
    contractor: 'City Public Works',
    contractorRating: 'A',
    completedLoads: 6,
    totalHours: 6.5,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_22222.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_22222.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_22222.pdf'
    }],
    notes: 'Approved with standard terms.',
    paymentTerms: 'Net 60',
    factorRate: 3.25,
    achFileName: 'ACH_20240115_WH_22222.ach',
    achGenerationDate: '2024-01-15',
    expectedPaymentDate: '2024-01-16'
  }, {
    id: 'req-008',
    truckingCompany: 'SLC Trucking Services',
    jobNumber: 'JOB-2023-076',
    jobName: 'Highway 89 Expansion',
    completionDate: '2024-01-12',
    requestDate: '2024-01-13',
    approvalDate: '2024-01-14',
    amount: 4100.75,
    invoiceNumber: 'INV-SLC-33333',
    status: 'approved',
    priority: 'high',
    contractor: 'State DOT',
    contractorRating: 'A+',
    completedLoads: 14,
    totalHours: 9.75,
    documents: [{
      type: 'invoice',
      fileName: 'invoice_33333.pdf'
    }, {
      type: 'timecard',
      fileName: 'timecard_33333.pdf'
    }, {
      type: 'load_tickets',
      fileName: 'load_tickets_33333.pdf'
    }],
    notes: 'Approved with premium client terms.',
    paymentTerms: 'Net 60',
    factorRate: 3.0,
    achFileName: 'ACH_20240114_SLC_33333.ach',
    achGenerationDate: '2024-01-14',
    expectedPaymentDate: '2024-01-15'
  }];
  const handleViewRequestDetails = (requestId: string) => {
    setSelectedRequestId(requestId);
    setShowRequestDetails(true);
  };
  const handleCloseRequestDetails = () => {
    setSelectedRequestId(null);
    setShowRequestDetails(false);
  };
  const tabs = [{
    id: 'pending',
    label: 'Pending Requests',
    icon: <ClockIcon size={18} data-id="element-2996" />,
    badge: pendingRequests.length
  }, {
    id: 'approved',
    label: 'Approved Requests',
    icon: <CheckCircleIcon size={18} data-id="element-2997" />
  }, {
    id: 'ach',
    label: 'ACH Generator',
    icon: <div size={18} data-id="element-2998" />
  }, {
    id: 'reports',
    label: 'Reports',
    icon: <BarChart4Icon size={18} data-id="element-2999" />
  }];
  // Find the selected request
  const selectedRequest = [...pendingRequests, ...approvedRequests].find(req => req.id === selectedRequestId);
  return <div className="p-4 lg:p-6 pt-16 lg:pt-6 bg-gray-50 min-h-screen" data-id="element-3000">
      <div className="mb-6" data-id="element-3001">
        <h1 className="text-2xl font-bold text-[#0F2A52] mb-1" data-id="element-3002">
          Factoring Dashboard
        </h1>
        <p className="text-gray-600" data-id="element-3003">
          Review and process factoring applications from trucking companies
        </p>
      </div>
      {/* Dashboard Stats */}
      {!showRequestDetails && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-id="element-3004">
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-3005">
            <div className="flex items-center justify-between" data-id="element-3006">
              <div data-id="element-3007">
                <p className="text-sm font-medium text-gray-500" data-id="element-3008">
                  Pending Requests
                </p>
                <p className="text-2xl font-bold text-[#0F2A52]" data-id="element-3009">
                  {stats.pendingRequests}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-3010">
                <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-3011" />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-3012">
            <div className="flex items-center justify-between" data-id="element-3013">
              <div data-id="element-3014">
                <p className="text-sm font-medium text-gray-500" data-id="element-3015">
                  Approved Today
                </p>
                <p className="text-2xl font-bold text-green-600" data-id="element-3016">
                  {stats.approvedToday}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full" data-id="element-3017">
                <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-3018" />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-3019">
            <div className="flex items-center justify-between" data-id="element-3020">
              <div data-id="element-3021">
                <p className="text-sm font-medium text-gray-500" data-id="element-3022">
                  Pending Amount
                </p>
                <p className="text-2xl font-bold text-[#0F2A52]" data-id="element-3023">
                  $
                  {stats.totalPending.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-3024">
                <DollarSignIcon className="h-6 w-6 text-blue-600" data-id="element-3025" />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-3026">
            <div className="flex items-center justify-between" data-id="element-3027">
              <div data-id="element-3028">
                <p className="text-sm font-medium text-gray-500" data-id="element-3029">
                  Approved Amount
                </p>
                <p className="text-2xl font-bold text-green-600" data-id="element-3030">
                  $
                  {stats.totalApprovedToday.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full" data-id="element-3031">
                <DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-3032" />
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-3033">
            <div className="flex items-center justify-between" data-id="element-3034">
              <div data-id="element-3035">
                <p className="text-sm font-medium text-gray-500" data-id="element-3036">ACH Files</p>
                <p className="text-2xl font-bold text-[#F58220]" data-id="element-3037">
                  {stats.achFilesGenerated}
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full" data-id="element-3038">
                <div className="h-6 w-6 text-[#F58220]" data-id="element-3039" />
              </div>
            </div>
          </div>
        </div>}
      {/* Tab Navigation */}
      {!showRequestDetails && <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-3040" />}
      {/* Tab Content */}
      {showRequestDetails ? <RequestDetails request={selectedRequest} onClose={handleCloseRequestDetails} data-id="element-3041" /> : <>
          {activeTab === 'pending' && <PendingRequests requests={pendingRequests} onViewDetails={handleViewRequestDetails} data-id="element-3042" />}
          {activeTab === 'approved' && <ApprovedRequests requests={approvedRequests} onViewDetails={handleViewRequestDetails} data-id="element-3043" />}
          {activeTab === 'ach' && <ACHGenerator requests={approvedRequests} data-id="element-3044" />}
          {activeTab === 'reports' && <div className="bg-white rounded-lg shadow p-6" data-id="element-3045">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2" data-id="element-3046">
                <BarChart4Icon size={20} className="text-[#0F2A52]" data-id="element-3047" />
                Reports & Analytics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3048">
                <div className="border rounded-lg p-4" data-id="element-3049">
                  <h3 className="font-medium mb-2 flex items-center gap-2" data-id="element-3050">
                    <CalendarIcon size={16} className="text-[#0F2A52]" data-id="element-3051" />
                    Monthly Reports
                  </h3>
                  <ul className="space-y-2" data-id="element-3052">
                    <li className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-3053">
                      <span data-id="element-3054">January 2024 Summary</span>
                      <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-3055">
                        <DownloadIcon size={14} data-id="element-3056" />
                        Download
                      </button>
                    </li>
                    <li className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-3057">
                      <span data-id="element-3058">December 2023 Summary</span>
                      <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-3059">
                        <DownloadIcon size={14} data-id="element-3060" />
                        Download
                      </button>
                    </li>
                    <li className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-3061">
                      <span data-id="element-3062">November 2023 Summary</span>
                      <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-3063">
                        <DownloadIcon size={14} data-id="element-3064" />
                        Download
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="border rounded-lg p-4" data-id="element-3065">
                  <h3 className="font-medium mb-2 flex items-center gap-2" data-id="element-3066">
                    <TruckIcon size={16} className="text-[#0F2A52]" data-id="element-3067" />
                    Trucking Company Reports
                  </h3>
                  <ul className="space-y-2" data-id="element-3068">
                    <li className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-3069">
                      <span data-id="element-3070">Mountain Valley Trucking</span>
                      <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-3071">
                        <DownloadIcon size={14} data-id="element-3072" />
                        Download
                      </button>
                    </li>
                    <li className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-3073">
                      <span data-id="element-3074">Wasatch Hauling</span>
                      <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-3075">
                        <DownloadIcon size={14} data-id="element-3076" />
                        Download
                      </button>
                    </li>
                    <li className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-3077">
                      <span data-id="element-3078">SLC Trucking Services</span>
                      <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-3079">
                        <DownloadIcon size={14} data-id="element-3080" />
                        Download
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </>}
    </div>;
};